// Kırılım penceresi boyut seçimi saf mantığı (spec §19 B3–B6): toplamın kök kategorisi, üç bölümlü
// seçenek listesi, boyut şablonundan üye ön doldurma (katalogdaki aynı adlı üye bağlanır), elle
// düzenlenmiş üyelerde şablon onayı, canlı örnek cümle ve benzer ad uyarısı. Bileşen yalnız
// görünüm + olay (BreakdownDimensionPicker / BreakdownGroupModal); kural burada.

import {
  KPI_BREAKDOWN_DIMENSIONS,
  KPI_BREAKDOWN_DIMENSION_PRESETS,
  KPI_ROOT_CODES,
  dimensionSectionsFor,
  normalizeMappingLabel,
  similarDimension,
  type KpiBreakdownDimension,
  type KpiRootCode,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";
import {
  newMemberDraft,
  slugForDimension,
  type GroupForm,
  type MemberDraft,
} from "./kpi-breakdown-group-form";
import { type SearchSelectOption } from "./search-select";

const C = KPI_BREAKDOWN_COPY.modal;
const OTHER = "diger";

/** Sözlükteki özel boyut (GET /kpi/breakdown/dimensions `custom[]` öğesinin okunan alanları). */
export interface CustomDimensionOption {
  slug: string;
  label: string;
  members: readonly string[];
  categories?: readonly KpiRootCode[];
}

export type DimensionChoice =
  | { kind: "preset"; slug: KpiBreakdownDimension }
  | { kind: "custom"; dim: CustomDimensionOption };

export function isPresetSlug(slug: string): slug is KpiBreakdownDimension {
  return (KPI_BREAKDOWN_DIMENSIONS as readonly string[]).includes(slug);
}

/**
 * Göstergenin kök kategori kodu: `parentId` zinciri köke yürünür, kökün `code`'u
 * `KPI_ROOT_CODES` içindeyse o; aksi (bulunamayan düğüm, döngü, başka kök) null.
 */
export function rootCodeOf(
  metricId: string,
  catalog: readonly { id: string; parentId: string | null; code: string }[],
): KpiRootCode | null {
  const byId = new Map(catalog.map((m) => [m.id, m]));
  let node = byId.get(metricId);
  const seen = new Set<string>();
  while (node && node.parentId !== null) {
    if (seen.has(node.id)) return null;
    seen.add(node.id);
    node = byId.get(node.parentId);
  }
  if (!node) return null;
  return (KPI_ROOT_CODES as readonly string[]).includes(node.code)
    ? (node.code as KpiRootCode)
    : null;
}

/** Seçicideki değer → seçim (hazır slug ya da sözlükteki özel boyut); bilinmeyen → null. */
export function choiceForValue(
  value: string,
  custom: readonly CustomDimensionOption[],
): DimensionChoice | null {
  if (isPresetSlug(value)) return { kind: "preset", slug: value };
  const dim = custom.find((c) => c.slug === value);
  return dim ? { kind: "custom", dim } : null;
}

/**
 * Üç bölümlü seçenekler (B3): "Bu gösterge için uygun" · "Diğer boyutlar" · "Sizin
 * tanımladıklarınız". Aynı üstte kullanılmış boyut pasif + "bu göstergede var" (Diğer hep açık).
 * Hazır seçenekte ipucu = örnek üyeler, `title` = ne zaman kullanılır.
 */
export function dimensionPickerOptions(
  rootCode: KpiRootCode | null,
  custom: readonly CustomDimensionOption[],
  used: ReadonlySet<string>,
  opts: { showOther?: boolean } = {},
): SearchSelectOption[] {
  const s = dimensionSectionsFor(rootCode, custom);
  const showOther = opts.showOther ?? true;
  const preset = (slug: KpiBreakdownDimension, group: string): SearchSelectOption => {
    const p = KPI_BREAKDOWN_DIMENSION_PRESETS[slug];
    const taken = slug !== OTHER && used.has(slug);
    return {
      value: slug,
      label: p.label,
      group,
      title: p.help,
      hint: taken ? C.dimensionUsed : p.example,
      ...(taken ? { disabled: true } : {}),
    };
  };
  return [
    ...s.suitable.map((slug) => preset(slug, C.sections.suitable)),
    ...(showOther ? s.other.map((slug) => preset(slug, C.sections.other)) : []),
    ...s.custom.map((d): SearchSelectOption => {
      const taken = used.has(d.slug);
      return {
        value: d.slug,
        label: d.label,
        group: C.sections.custom,
        title: C.customHelp,
        hint: taken ? C.dimensionUsed : customExample(d.members),
        ...(taken ? { disabled: true } : {}),
      };
    }),
  ];
}

/**
 * K-B6 (TODO-KPI-149): "Diğer boyutlar" bölümü uygun/özel bölüm doluyken katlanabilir ve açılışta
 * kapalıdır; seçili boyut o bölümdeyse zorunlu açık (seçili değer listede görünür); uygun/özel
 * bölüm boşsa (kök yok) liste zaten yalnız "Diğer"dir → katlama yok.
 */
export function otherDimensionState(
  rootCode: KpiRootCode | null,
  custom: readonly CustomDimensionOption[],
  selected: string,
): { count: number; collapsible: boolean; forcedOpen: boolean } {
  const s = dimensionSectionsFor(rootCode, custom);
  const count = s.other.length;
  const forcedOpen = (s.other as readonly string[]).includes(selected);
  return { count, collapsible: count > 0 && s.suitable.length + s.custom.length > 0, forcedOpen };
}

const customExample = (members: readonly string[]): string =>
  members.length > 0 ? C.exampleMembers(members.join(", ")) : C.customNoMembers;

/** Seçili boyutun görünür yardım satırı (B4): ne zaman kullanılır + örnek üyeler. */
export function dimensionHelp(
  form: Pick<GroupForm, "dimension">,
  custom: readonly CustomDimensionOption[],
): { help: string; example: string } | null {
  const choice = choiceForValue(form.dimension, custom);
  if (!choice) return null;
  if (choice.kind === "preset") {
    const p = KPI_BREAKDOWN_DIMENSION_PRESETS[choice.slug];
    return { help: p.help, example: p.example };
  }
  return { help: C.customHelp, example: customExample(choice.dim.members) };
}

const sameLabel = (a: string, b: string): boolean => {
  const na = normalizeMappingLabel(a);
  return na !== "" && na === normalizeMappingLabel(b);
};

const templateOf = (choice: DimensionChoice): readonly string[] =>
  choice.kind === "preset"
    ? KPI_BREAKDOWN_DIMENSION_PRESETS[choice.slug].members
    : choice.dim.members;

/**
 * Boyut seçimi (B5): boyut + grup adı (elle değişmediyse) + özel boyut adı; üyeler şablondan
 * dolar — katalogda aynı (normalize) adlı kalem varsa o bağlanır (yeni üye yaratılmaz); şablonu
 * boş boyutta iki boş satır. Bağlama yalnız mevcut grubun listesinde anlamlı (spec §19 B5 notu).
 */
export function applyDimensionTemplate(
  form: GroupForm,
  choice: DimensionChoice,
  catalogMembers: readonly { metricId: string; label: string }[],
): GroupForm {
  const slug = choice.kind === "preset" ? choice.slug : choice.dim.slug;
  const dimensionLabel = choice.kind === "custom" ? choice.dim.label : "";
  const next: GroupForm = { ...form, dimension: slug, dimensionLabel };
  if (slug === OTHER) {
    // Diğer: önceki "Diğer" adı/kodu korunur (kullanıcı boyutlar arasında gidip geldiyse)
    next.dimensionLabel = form.dimension === OTHER ? form.dimensionLabel : "";
    if (!form.slugTouched) next.customSlug = slugForDimension(next.dimensionLabel);
    if (!form.labelTouched) next.label = next.dimensionLabel;
  } else if (!form.labelTouched) {
    next.label =
      choice.kind === "preset"
        ? KPI_BREAKDOWN_DIMENSION_PRESETS[slug as KpiBreakdownDimension].label
        : dimensionLabel;
  }
  const used = new Set<string>();
  const members: MemberDraft[] = templateOf(choice).map((label) => {
    const hit = catalogMembers.find((m) => !used.has(m.metricId) && sameLabel(m.label, label));
    if (hit) used.add(hit.metricId);
    return { ...newMemberDraft(), label: hit ? hit.label : label, metricId: hit?.metricId ?? null };
  });
  while (members.length < 2) members.push(newMemberDraft());
  return { ...next, members, membersTouched: false };
}

/** Boyut değişimi şablonu uygulamadan önce onay ister mi: üyeler elle düzenlendi ve dolu. */
export function templateReplaceNeedsConfirm(form: GroupForm): boolean {
  return (
    form.membersTouched && form.members.some((m) => m.metricId !== null || m.label.trim() !== "")
  );
}

/**
 * Canlı örnek cümle (B6): tam + Topla → "Kadın + Erkek = Toplam çalışan"; tam + Ortala →
 * "(Kadın + Erkek) / 2 = Toplam çalışan"; tam değil ya da hesaplama yok → yalnız gösterim metni.
 * `check` (toplamı başka bir grup hesaplıyor) → "Kontrol: Kadın + Erkek, Toplam çalışan ile
 * karşılaştırılır". Üye yokken yer tutucu "… + …".
 */
export function liveSentence(
  form: Pick<GroupForm, "complete" | "rollup" | "members">,
  totalLabel: string,
  opts: { check?: boolean } = {},
): string {
  if (!form.complete || form.rollup === "none") return C.live.none;
  const names = form.members.map((m) => m.label.trim()).filter((l) => l !== "");
  const list = names.length > 0 ? names.join(" + ") : C.live.emptyMembers;
  const parts = form.rollup === "avg" ? C.live.avgParts(list, Math.max(names.length, 2)) : list;
  return opts.check ? C.live.check(parts, totalLabel) : C.live.sum(parts, totalLabel);
}

/**
 * Benzer boyut (B2 uyarısı): yazılan ad hazır (Diğer hariç) ya da özel bir boyuta benziyorsa o.
 * Sunucudaki `?similarTo=` ile aynı kural (shared `similarDimension`); engellemez.
 */
export function similarHint(
  label: string,
  custom: readonly CustomDimensionOption[],
): { slug: string; label: string } | null {
  if (label.trim() === "") return null;
  const presets = KPI_BREAKDOWN_DIMENSIONS.filter((s) => s !== OTHER).map((slug) => ({
    slug,
    label: KPI_BREAKDOWN_DIMENSION_PRESETS[slug].label,
  }));
  const hit = similarDimension(label, [...presets, ...custom]);
  return hit ? { slug: hit.slug, label: hit.label } : null;
}
